import React, { useCallback, useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import Loading from "../../../components/LoadingPage/Loading";
import McKessonPageHeader from "../McKessonPageHeader/McKessonPageHeader";
import CommonPagination from "../../../global/Pagination/CommonPagination";
import useMckesson from "../../../Hooks/useMckesson";
import "./McItemsMaster.scss";

interface McItemRecord {
  id: string;
  itemId: string;
  description: string;
  supplierId: string;
  manufacturerId: string;
  manufacturerName: string;
  atomicUom: string;
  valid: boolean;
}

interface McItemUom {
  id: string;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string;
  createdBy: string;
  updatedBy: string;
  mcKessonItemId: string;
  currency: string;
  price: number;
  quantityAvailable: number;
  per: number;
  atomic: number;
  units: string;
}

const defaultFilter = { searchText: "", active: true };

const McItemsMaster: React.FC = () => {
  const [searchVal, setSearchVal] = useState({ ...defaultFilter });
  const [items, setItems] = useState<McItemRecord[]>([]);
  const [uomData, setUomData] = useState<McItemUom[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [itemId, setItemId] = useState<string>("");
  const [itemIdError, setItemIdError] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string>("ItemId");
  const [sortDirection, setSortDirection] = useState<string>("asc");

  const {
    getMckessonItemsAPI,
    getMckessonItemsLoading,
    getMckessonUomsAPI,
    getMckessonUomsLoading,
    postMckessonItemSearchAPI,
    postMckessonItemSearchLoading,
    postMckessonItemFeedAPI,
    postMckessonItemFeedLoading,
    postMckessonItemLookupAPI,
    postMckessonItemLookupLoading,
  } = useMckesson();

  const isLoading = getMckessonItemsLoading || getMckessonUomsLoading || postMckessonItemSearchLoading || postMckessonItemFeedLoading || postMckessonItemLookupLoading;

  const loadItems = useCallback(async (page: number = 1) => {
    try {
      const response = await getMckessonItemsAPI({
        searchText: searchVal.searchText,
        itemId: itemId,
        active: searchVal.active,
        sortBy: sortColumn,
        isDescending: sortDirection === "desc",
        pageNumber: page,
        pageSize: pageSize
      });
      if (response) {
        setItems(response.result || []);
        let total = response.result?.length || 0;
        if (response.message) {
          const match = response.message.match(/Total Records:\s*(\d+)/);
          if (match) {
            total = parseInt(match[1], 10);
          }
        }
        setTotalRecords(total);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [searchVal, pageSize, itemId, sortColumn, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
    loadItems(1);
  }, [loadItems]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadItems(page);
  };

  const handleLookUp = async () => {
    if (!itemId?.trim()) {
      setItemIdError("Item Id is required for Lookup");
      return;
    }
    setItemIdError("");
    try {
      const result = await postMckessonItemLookupAPI({
        itemId: itemId,
      });
      if (result) {
        const data = Array.isArray(result) ? result : [result];
        setItems(data);
        setTotalRecords(data.length);
        setCurrentPage(1);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleItemSearch = async () => {
    try {
      const result = await postMckessonItemSearchAPI({
        query: searchVal.searchText,
        pageSize: 100,
        offset: 0,
        sort: { order: "asc", by: "ItemId" },
      });
      if (result) {
        setItems(result);
        setTotalRecords(result.length);
        setCurrentPage(1);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleItemFeed = async () => {
    try {
      const result = await postMckessonItemFeedAPI({
        source: { type: "", text: searchVal.searchText },
        pageSize: 100,
        offset: 0,
      });
      if (result) {
        setItems(result);
        setTotalRecords(result.length);
        setCurrentPage(1);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleRowClick = async (item: McItemRecord) => {
    setSelectedItemId(item.id);
    try {
      const result = await getMckessonUomsAPI({
        mcKessonItemId: item.id,
        searchText: "",
        active: true,
      });
      if (result) {
        setUomData(result);
      } else {
        setUomData([]);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnName);
      setSortDirection("asc");
    }
  };

  return (
    <div className="mc-items-master-wrapper">
      {isLoading && <Loading />}

      <McKessonPageHeader
        title="McKesson Items Master"
        disRecords={items.length}
        totalRecords={totalRecords}
        searchVal={searchVal}
        setSearchVal={setSearchVal}
        defaultFilter={defaultFilter}
        showClearButton={false}
        actionButtons={[
          { label: "Look Up (McKesson)", onClick: handleLookUp },
          { label: "Item Search (McKesson)", onClick: handleItemSearch },
          { label: "Item Feed (McKesson)", onClick: handleItemFeed },
        ]}
        itemId={itemId}
        onItemIdChange={(val) => {
          setItemId(val);
          if (val.trim()) setItemIdError("");
        }}
        itemIdError={itemIdError}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      {/* Items Table */}
      <div className="listing-table-wrapper">
        <Table responsive="md" hover>
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => handleSort('ItemId')}>Item Id {sortColumn === 'ItemId' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('Description')}>Description {sortColumn === 'Description' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('SupplierId')}>Supplier Id {sortColumn === 'SupplierId' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('ManufacturerId')}>Manufacturer Id {sortColumn === 'ManufacturerId' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('ManufacturerName')}>Manufacturer Name {sortColumn === 'ManufacturerName' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('AtomicUom')}>Atomic Uom {sortColumn === 'AtomicUom' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('Valid')}>Valid? {sortColumn === 'Valid' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr
                  key={item.id}
                  className={selectedItemId === item.id ? "selected" : ""}
                  onClick={() => handleRowClick(item)}
                >
                  <td>{item.itemId}</td>
                  <td>{item.description}</td>
                  <td>{item.supplierId}</td>
                  <td>{item.manufacturerId}</td>
                  <td>{item.manufacturerName}</td>
                  <td>{item.atomicUom}</td>
                  <td>{item.valid ? "Yes" : "No"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <CommonPagination
        totalRecords={totalRecords}
        onChange={handlePageChange}
        perPage={pageSize}
        currentPage={currentPage}
      />

      {/* UOM Detail Table */}
      {uomData.length > 0 && (
        <div className="mc-items-pricing-section">
          <div className="listing-table-wrapper pricing-table">
            <Table responsive="md" hover>
              <thead>
                <tr>
                  <th>Quantity Available</th>
                  <th>Price</th>
                  <th>Currency</th>
                  <th>Per</th>
                  <th>Atomic</th>
                  <th>Units</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {uomData.map((uom, index) => (
                  <tr key={index}>
                    <td>{uom.quantityAvailable?.toFixed(4)}</td>
                    <td>{uom.price?.toFixed(2)}</td>
                    <td>{uom.currency}</td>
                    <td>{uom.per?.toFixed(2)}</td>
                    <td>{uom.atomic?.toFixed(2)}</td>
                    <td>{uom.units}</td>
                    <td>{uom.createdBy}</td>
                    <td>{uom.createdOnUtc}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default McItemsMaster;
